/**
 * Direct pixel firing module.
 * Fires platform-translated events to Meta (fbq), TikTok (ttq),
 * Google Ads (gtag), Google Analytics (gtag), and Tracklution (tlq).
 *
 * Platform event names come from the registry entry's `adEventNames`
 * translations. All configured platforms fire independently. Dealers may run
 * GTM alongside direct pixels during migration. Dedup is handled by ad
 * platforms via event_id.
 */
/** Convert GA4 items[] to Meta contents[] */
function toMetaContents(items) {
    return items.map((item) => ({
        id: item.item_id,
        quantity: item.quantity,
        item_price: item.price,
    }));
}
/** Convert GA4 items[] to TikTok contents[] */
function toTikTokContents(items) {
    return items.map((item) => ({
        content_id: item.item_id,
        content_name: item.item_name,
        quantity: item.quantity,
        price: item.price,
    }));
}
function fireMetaPixel(eventDef, ecommerce, config, eventId) {
    const metaName = eventDef.adEventNames?.meta;
    if (!metaName)
        return;
    if (!config.meta?.pixelId)
        return;
    if (typeof window === 'undefined' || typeof window.fbq !== 'function')
        return;
    window.fbq('track', metaName, {
        content_type: 'product',
        contents: toMetaContents(ecommerce.items),
        currency: ecommerce.currency,
        value: ecommerce.value,
        ...(ecommerce.transaction_id && { order_id: ecommerce.transaction_id }),
    }, ...(eventId ? [{ eventID: eventId }] : []));
}
function fireTikTokPixel(eventDef, ecommerce, config, eventId) {
    const tiktokName = eventDef.adEventNames?.tiktok;
    if (!tiktokName)
        return;
    if (!config.tiktok?.pixelId)
        return;
    if (typeof window === 'undefined' || typeof window.ttq?.track !== 'function')
        return;
    window.ttq.track(tiktokName, {
        content_type: 'product',
        contents: toTikTokContents(ecommerce.items),
        currency: ecommerce.currency,
        value: ecommerce.value,
    }, ...(eventId ? [{ event_id: eventId }] : []));
}
/** Shared gtag event fire for Google Ads and Google Analytics. */
function fireGtagEvent(eventDef, ecommerce, sendTo, eventId) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function')
        return;
    // Fire the GA4/Google Ads standard name (view_item, add_to_cart, …) so the pixel is
    // attributed as a standard ecommerce conversion. Falls back to the canonical when no
    // translation is set — matches the dataLayer name resolution in conversion-tracking.
    window.gtag('event', eventDef.adEventNames?.gtm ?? eventDef.canonical, {
        send_to: sendTo,
        currency: ecommerce.currency,
        value: ecommerce.value,
        items: ecommerce.items,
        transaction_id: ecommerce.transaction_id ?? eventId,
    });
}
function fireGoogleAdsPixel(eventDef, ecommerce, config, eventId) {
    if (!config.googleAds?.tagId)
        return;
    fireGtagEvent(eventDef, ecommerce, config.googleAds.tagId, eventId);
}
function fireGoogleAnalytics(eventDef, ecommerce, config, eventId) {
    if (!config.googleAnalytics?.measurementId)
        return;
    fireGtagEvent(eventDef, ecommerce, config.googleAnalytics.measurementId, eventId);
}
function fireTracklutionEvent(eventDef, ecommerce, config) {
    const tracklutionName = eventDef.adEventNames?.tracklution;
    if (!tracklutionName)
        return;
    if (!config.tracklution?.siteId)
        return;
    if (typeof window === 'undefined' || typeof window.tlq !== 'function')
        return;
    window.tlq('track', tracklutionName, {
        value: ecommerce.value,
        currency: ecommerce.currency,
        ...(ecommerce.transaction_id && { order_id: ecommerce.transaction_id }),
    });
}
/**
 * Fire a Google gtag event and wait for callback (with timeout).
 * Used for purchase where we need to wait before redirecting.
 */
function gtagWithCallback(eventName, data, config, timeoutMs = 5000) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return Promise.resolve();
    }
    if (!config.googleAds?.tagId) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
            resolve();
        }, timeoutMs);
        window.gtag('event', eventName, {
            ...data,
            send_to: config.googleAds?.tagId,
            event_callback() {
                clearTimeout(timeoutId);
                resolve();
            },
        });
    });
}
/**
 * Fire all configured direct pixels for a custom (non-ecommerce) event.
 * Sends the event name to each platform without ecommerce payload.
 */
function fireCustomPixels(eventDef, config, data, eventId, contactInfo) {
    const { meta, tiktok, tracklution } = eventDef.adEventNames ?? {};
    const fires = [];
    if (meta && config.meta?.pixelId) {
        fires.push([
            'meta',
            () => {
                if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
                    window.fbq('trackCustom', meta, data, ...(eventId ? [{ eventID: eventId }] : []));
                }
            },
        ]);
    }
    if (tiktok && config.tiktok?.pixelId) {
        fires.push([
            'tiktok',
            () => {
                if (typeof window !== 'undefined' && typeof window.ttq?.track === 'function') {
                    window.ttq.track(tiktok, data, ...(eventId ? [{ event_id: eventId }] : []));
                }
            },
        ]);
    }
    if (tracklution && config.tracklution?.siteId) {
        fires.push([
            'tracklution',
            () => {
                if (typeof window !== 'undefined' && typeof window.tlq === 'function') {
                    // Attach lead contact details before the event so Tracklution can match
                    // the lead to a person for server-side attribution.
                    if (contactInfo)
                        window.tlq('set', 'ContactInfo', contactInfo);
                    window.tlq('track', tracklution, data);
                }
            },
        ]);
    }
    for (const [platform, fire] of fires) {
        try {
            fire();
        }
        catch (err) {
            // eslint-disable-next-line no-console -- shared library, no RequestContext available
            console.warn(`[ConversionTracking] ${platform} custom pixel fire failed:`, err);
        }
    }
}
/**
 * Fire all configured direct pixels for an ecommerce event.
 * Each platform is isolated — one SDK failure won't prevent others from firing.
 */
function fireAllPixels(eventDef, ecommerce, config, eventId) {
    const fires = [
        ['meta', () => fireMetaPixel(eventDef, ecommerce, config, eventId)],
        ['tiktok', () => fireTikTokPixel(eventDef, ecommerce, config, eventId)],
        ['googleAds', () => fireGoogleAdsPixel(eventDef, ecommerce, config, eventId)],
        ['googleAnalytics', () => fireGoogleAnalytics(eventDef, ecommerce, config, eventId)],
        ['tracklution', () => fireTracklutionEvent(eventDef, ecommerce, config)],
    ];
    for (const [platform, fire] of fires) {
        try {
            fire();
        }
        catch (err) {
            // eslint-disable-next-line no-console -- shared library, no RequestContext available
            console.warn(`[ConversionTracking] ${platform} pixel fire failed:`, err);
        }
    }
}

export { fireAllPixels, fireCustomPixels, fireGoogleAdsPixel, fireGoogleAnalytics, fireMetaPixel, fireTikTokPixel, fireTracklutionEvent, gtagWithCallback };
//# sourceMappingURL=pixels.js.map
